import React, { useMemo, useRef } from "react"
import L from "leaflet" // Ensure you have Leaflet imported to use L.icon
import { Marker, Popup, useMapEvents } from "react-leaflet"
import * as turf from "@turf/turf"
import { formatLatLngDM } from "@/utils/geoFormat"
import PlannerCursorHint from "@/features/voyagePlanner/PlannerCursorHint"
import PlaceIcon from "@mui/icons-material/Place"
import ReactDOMServer from "react-dom/server"
import markerIconBlue from "../../assets/images/marker/marker-icon-blue.png"
import {
  useRenderableRoute,
  useRouteStore,
} from "@/features/route/state/RouteStore.jsx"
import { normalizeSketchRoute } from "@/features/route/state/RouteAdapters.jsx"
import {
  insertManualWaypoint,
  isManualDraft,
  manualLegPoints,
  manualMiddlePoints,
  manualPointLabel,
  nearestManualLeg,
  manualRoutePoints,
  removeManualEndpoint,
  sameLonLat,
  setManualStartLegtype,
} from "@/features/voyagePlanner/manualSketch"

const markerRed = L.divIcon({
  className: "css-icon",
  html: ReactDOMServer.renderToString(
    <PlaceIcon style={{ color: "red", fontSize: 40, stroke: "white", strokeWidth: 1 }} />
  ),
  iconSize: [25, 41],
  iconAnchor: [0.5 * 40, 40],
})

const markerGreen = L.divIcon({
  className: "css-icon",
  html: ReactDOMServer.renderToString(
    <PlaceIcon style={{ color: "green", fontSize: 40, stroke: "white", strokeWidth: 1 }} />
  ),
  iconSize: [25, 41],
  iconAnchor: [0.5 * 40, 40],
})

const markerBlue = L.icon({
  iconUrl: markerIconBlue,
  iconSize: [25, 41], // Size of the icon
  iconAnchor: [12.5, 41], // point of the icon which will correspond to marker's location
})

export default function VoyagePlannerController({
  openPanels,
  voyagePlannerPoints,
  setVoyagePlannerPoints,
  plannerDraftSlot,
  startEndpoint,
  setStartEndpoint,
  finishEndpoint,
  setFinishEndpoint,
  setStartInput,
  setFinishInput,
  toogleButtonsOnOff,
  voyageActive,
  setVoyageActive,
  switchVoyagePlannerActive,
  switchManualPathing,
  openWaypointCard,
}) {
  const isVoyagePlannerEnabled = toogleButtonsOnOff.includes("VoyagePlanner")
  const plannerFeatures = voyagePlannerPoints?.features ?? []
  // Time/fuel mode only: points appended from outside (RNW legs).
  const autoMiddleFeatures =
    !switchManualPathing && plannerFeatures.length > 2
      ? plannerFeatures.slice(1, -1)
      : []

  const { replaceRoute } = useRouteStore()
  const slotRouteId = plannerDraftSlot?.routeId ?? null
  const slotRoute = useRenderableRoute(slotRouteId)
  // Only a manual draft contributes waypoints; a time/fuel draft gets replaced.
  const manualRoute = isManualDraft(slotRoute) ? slotRoute : null

  // Stored coord stays as-clicked so the marker renders where the user
  // clicked. The displayed text uses the shared formatter, which normalises
  // longitude to [-180, 180] for display only.
  const createEndpoint = (lng, lat) => ({
    coordinates: [lng, lat],
    portId: null,
    text: formatLatLngDM(lng, lat),
  })
  const reindexFeatures = (features) =>
    features.map((point, index) =>
      turf.point(point.geometry.coordinates, {
        ...point.properties,
        number: index + 1,
        extended_number: 0,
      })
    )
  const applyStart = (endpoint) => {
    setStartEndpoint(endpoint)
    setStartInput(endpoint?.text ?? "")
  }
  const applyFinish = (endpoint) => {
    setFinishEndpoint(endpoint)
    setFinishInput(endpoint?.text ?? "")
  }
  const writeSketch = (features) => {
    if (slotRouteId == null) return false
    replaceRoute(slotRouteId, normalizeSketchRoute(slotRouteId, features))
    return true
  }
  const isNear = (a, b) =>
    Array.isArray(a) &&
    Array.isArray(b) &&
    turf.distance(turf.point(a), turf.point(b), { units: "meters" }) < 10

  // Memoised so the leg-sample cache in nearestManualLeg survives unrelated re-renders.
  const legPoints = useMemo(
    () =>
      startEndpoint && finishEndpoint
        ? manualLegPoints(manualRoute, {
            start: startEndpoint.coordinates,
            finish: finishEndpoint.coordinates,
          })
        : [],
    [manualRoute, startEndpoint, finishEndpoint],
  )
  const nearestLegAt = (lonLat) =>
    nearestManualLeg(legPoints, lonLat, ([lng, lat]) =>
      map.latLngToContainerPoint(L.latLng(lat, lng))
    )

  const handleManualClick = (lng, lat) => {
    const clickPoint = [lng, lat]
    if (!startEndpoint) {
      applyStart(createEndpoint(lng, lat))
      return
    }
    if (!finishEndpoint) {
      const lastPoint =
        manualMiddlePoints(manualRoute).at(-1)?.geometry?.coordinates ??
        startEndpoint.coordinates
      if (isNear(lastPoint, clickPoint)) return
      applyFinish(createEndpoint(lng, lat))
      return
    }
    // The finish stays fixed: a click splits the nearest leg.
    const legIndex = nearestLegAt(clickPoint)
    if (legIndex < 0 || legPoints.some((p) => isNear(p.coords, clickPoint))) return
    writeSketch(
      insertManualWaypoint(manualRoute, {
        start: startEndpoint.coordinates,
        finish: finishEndpoint.coordinates,
        point: clickPoint,
        legIndex,
      })
    )
  }

  // Manual mode: removing an endpoint promotes the neighbouring waypoint.
  const handleEndpointRemove = (which) => {
    const promoted =
      switchManualPathing && startEndpoint && finishEndpoint
        ? removeManualEndpoint(manualRoute, which, {
            start: startEndpoint.coordinates,
            finish: finishEndpoint.coordinates,
          })
        : null
    const endpoint =
      promoted && writeSketch(promoted.features)
        ? createEndpoint(promoted.endpoint[0], promoted.endpoint[1])
        : null
    if (which === "start") applyStart(endpoint)
    else applyFinish(endpoint)
  }

  const handleStartLegtypeChange = (legtype) => {
    if (!startEndpoint || !finishEndpoint) return
    writeSketch(
      setManualStartLegtype(manualRoute, legtype, {
        start: startEndpoint.coordinates,
        finish: finishEndpoint.coordinates,
      })
    )
  }

  // The endpoint card outlives this render: its actions call the latest handlers.
  const endpointActionsRef = useRef({})
  endpointActionsRef.current = {
    remove: handleEndpointRemove,
    startLegtype: handleStartLegtypeChange,
  }

  const openEndpointCard = (which) => {
    const endpoint = which === "start" ? startEndpoint : finishEndpoint
    if (!endpoint?.coordinates || !openWaypointCard) return
    const points = manualRoutePoints(slotRoute)
    const routePoint = which === "start" ? points[0] : points.at(-1)
    // On the draft's own end point the card follows it live; otherwise a bare point.
    const feature =
      routePoint && sameLonLat(routePoint.geometry?.coordinates, endpoint.coordinates)
        ? routePoint
        : turf.point(endpoint.coordinates, { type: "original_point" })
    const label = which === "start" ? "Start" : "Finish"
    const [lng, lat] = endpoint.coordinates
    openWaypointCard(
      feature,
      { lat, lng },
      "Pathfinder",
      slotRoute?.points?.vesselName ?? "Voyage Planner",
      null,
      {
        label,
        manual: switchManualPathing,
        sketch: slotRoute?.sketch === true,
        deleteLabel: `Remove ${label.toLowerCase()} point`,
        onDelete: () => endpointActionsRef.current.remove(which),
        onLegtypeChange:
          which === "start" && switchManualPathing && finishEndpoint
            ? (legtype) => endpointActionsRef.current.startLegtype(legtype)
            : null,
      },
    )
  }

  const clicksPlace = openPanels.voyagePlanner && voyageActive && switchVoyagePlannerActive

  const map = useMapEvents({
    click: (e) => {
      if (e.originalEvent && e.originalEvent.target.closest(".vessel-table-paper")) {
        return
      }

      if (!clicksPlace) {
        setVoyageActive(false)
        return
      }

      if (switchManualPathing) {
        handleManualClick(e.latlng.lng, e.latlng.lat)
        return
      }

      const clickedEndpoint = createEndpoint(e.latlng.lng, e.latlng.lat)
      if (!startEndpoint) applyStart(clickedEndpoint)
      else if (!finishEndpoint) applyFinish(clickedEndpoint)
    },
  })

  const handleMiddleRightClick = (index) => {
    const filteredPoints = plannerFeatures.filter((_, i) => i !== index)
    setVoyageActive(true)
    setVoyagePlannerPoints(turf.featureCollection(reindexFeatures(filteredPoints)))
  }

  const handleMiddleDrag = (index, newLatLng) => {
    if (!isVoyagePlannerEnabled) return
    const updatedPoints = plannerFeatures.map((point, i) =>
      i === index
        ? turf.point([newLatLng.lng, newLatLng.lat], point.properties)
        : point
    )
    setVoyagePlannerPoints(turf.featureCollection(reindexFeatures(updatedPoints)))
  }

  const handleMiddleLegtype = (index, style) => {
    const updatedPoints = plannerFeatures.map((point, i) =>
      i === index
        ? turf.point(point.geometry.coordinates, {
            ...point.properties,
            legType: style,
            legtype: style,
          })
        : point
    )
    setVoyagePlannerPoints(turf.featureCollection(reindexFeatures(updatedPoints)))
  }

  const cursorHint = !clicksPlace
    ? null
    : !startEndpoint
      ? "Click to set departure"
      : !finishEndpoint
        ? "Click to set arrival"
        : switchManualPathing
          ? ({ lat, lng }) => {
              const leg = nearestLegAt([lng, lat])
              return leg < 0
                ? null
                : `Click to add waypoint · ${manualPointLabel(legPoints.length, leg)} → ${manualPointLabel(legPoints.length, leg + 1)}`
            }
          : null

  // Endpoint markers are planner tools: only while its panel is open.
  return !isVoyagePlannerEnabled || !switchVoyagePlannerActive || !openPanels.voyagePlanner ? null : (
    <>
      <PlannerCursorHint text={cursorHint} />
      {startEndpoint?.coordinates ? (
        <Marker
          key="voyage-start-endpoint"
          position={{
            lat: startEndpoint.coordinates[1],
            lng: startEndpoint.coordinates[0],
          }}
          icon={markerGreen}
          draggable={true}
          eventHandlers={{
            dragend: (e) => {
              const { lng, lat } = e.target.getLatLng()
              applyStart(createEndpoint(lng, lat))
            },
            click: () => openEndpointCard("start"),
            contextmenu: () => openEndpointCard("start"),
          }}
        />
      ) : null}

      {autoMiddleFeatures.map((point, idx) => (
        <Marker
          key={point.properties.number ?? `middle-${idx}`}
          position={{ lat: point.geometry.coordinates[1], lng: point.geometry.coordinates[0] }}
          icon={markerBlue}
          draggable={true}
          eventHandlers={{
            contextmenu: () => handleMiddleRightClick(idx + 1),
            dragend: (e) => handleMiddleDrag(idx + 1, e.target.getLatLng()),
          }}
        >
          <Popup>
            WP {point.properties.number} <br /> Latitude: {point.geometry.coordinates[1].toFixed(2)}, Longitude:{" "}
            {point.geometry.coordinates[0].toFixed(2)} <br />
            <select
              value={point.properties.legType}
              onChange={(e) => handleMiddleLegtype(idx + 1, e.target.value)}
            >
              <option value="rhumbline">Rhumbline</option>
              <option value="greatcircle">Great Circle</option>
            </select>
          </Popup>
        </Marker>
      ))}

      {finishEndpoint?.coordinates ? (
        <Marker
          key="voyage-finish-endpoint"
          position={{
            lat: finishEndpoint.coordinates[1],
            lng: finishEndpoint.coordinates[0],
          }}
          icon={markerRed}
          draggable={true}
          eventHandlers={{
            dragend: (e) => {
              const { lng, lat } = e.target.getLatLng()
              applyFinish(createEndpoint(lng, lat))
            },
            click: () => openEndpointCard("finish"),
            contextmenu: () => openEndpointCard("finish"),
          }}
        />
      ) : null}
    </>
  )
}
